import { useCallback, useEffect, useRef, useState } from 'react';
import { useToast } from '@chakra-ui/react';
import { SAKTI_CONFIG_DEFAULTS } from '../apiConfig';
import SaktiConfigApiService from '../services/saktiConfigApiService';
import { extractApiError } from '../utils/apiError';
import { SaktiSyncEntity, SaktiSyncJob, SaktiSyncStatus } from '../types';

// "all" is the sentinel the worker accepts for a full, dependency-ordered sync.
export const SYNC_ALL_SENTINEL = 'all';

// Give up polling after this many consecutive status-less failures (e.g. network
// blips) so a dead worker never leaves the UI spinning forever.
const MAX_POLL_FAILURES = 5;

const isTerminal = (status: SaktiSyncStatus): boolean =>
  status === 'completed' || status === 'failed';

// Any backend exposing the same entity-list / job / poll shapes can drive this hook
// (Sakti config gateway by default, insight master sync in the admin panel).
export type SyncJobApi = Pick<
  typeof SaktiConfigApiService,
  'listSyncEntities' | 'startSyncJob' | 'getSyncJob'
>;

const describeSaktiSuccess = (job: SaktiSyncJob): string =>
  `${job.total_synced.toLocaleString('id-ID')} baris tersinkron. Cache Redis telah diperbarui.`;

interface UseSaktiSyncResult {
  entities: SaktiSyncEntity[];
  loadingEntities: boolean;
  entitiesError: string | null;
  selected: string[];
  job: SaktiSyncJob | null;
  isSyncing: boolean;
  toggleEntity: (name: string) => void;
  selectAllEntities: () => void;
  clearSelection: () => void;
  syncAll: () => Promise<void>;
  syncSelected: () => Promise<void>;
  reloadEntities: () => Promise<void>;
}

export const useSaktiSync = (
  api: SyncJobApi = SaktiConfigApiService,
  describeSuccess: (job: SaktiSyncJob) => string = describeSaktiSuccess
): UseSaktiSyncResult => {
  const toast = useToast();

  const [entities, setEntities] = useState<SaktiSyncEntity[]>([]);
  const [loadingEntities, setLoadingEntities] = useState<boolean>(true);
  const [entitiesError, setEntitiesError] = useState<string | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [job, setJob] = useState<SaktiSyncJob | null>(null);
  const [isSyncing, setIsSyncing] = useState<boolean>(false);

  // Refs so the polling callback never reads stale state and we can cancel cleanly.
  const mountedRef = useRef<boolean>(true);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Count consecutive status-less poll failures (reset on any successful poll).
  const pollFailuresRef = useRef<number>(0);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // Cancel polling on unmount and block any late setState.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      clearTimer();
    };
  }, [clearTimer]);

  const notifyFinished = useCallback(
    (finished: SaktiSyncJob) => {
      if (finished.status === 'completed') {
        toast({
          title: 'Sinkronisasi selesai',
          description: describeSuccess(finished),
          status: 'success',
          duration: 5000,
          isClosable: true,
          position: 'top-right',
        });
        return;
      }

      const failedEntity = finished.entities.find((entity) => entity.status === 'failed');
      toast({
        title: 'Sinkronisasi gagal',
        description: finished.error || failedEntity?.error || 'Terjadi kesalahan saat sinkronisasi.',
        status: 'error',
        duration: 7000,
        isClosable: true,
        position: 'top-right',
      });
    },
    [describeSuccess, toast]
  );

  const beginPolling = useCallback(
    (jobId: string) => {
      clearTimer();
      pollFailuresRef.current = 0;
      timerRef.current = setInterval(async () => {
        try {
          const updated = await api.getSyncJob(jobId);
          if (!mountedRef.current) {
            return;
          }

          pollFailuresRef.current = 0;
          setJob(updated);

          if (isTerminal(updated.status)) {
            clearTimer();
            setIsSyncing(false);
            notifyFinished(updated);
          }
        } catch (err) {
          if (!mountedRef.current) {
            return;
          }

          const { status, message } = extractApiError(err);
          // Decide whether to stop polling. A dead worker mid-sync does NOT surface
          // as 404: the reporting relay returns 502 (worker unreachable) and other
          // upstream faults are 5xx. Treat 404 (job gone after restart), any 5xx,
          // and a run of status-less network errors as terminal — otherwise the UI
          // would spin forever during the exact long-running sync we must guard.
          pollFailuresRef.current = status === undefined ? pollFailuresRef.current + 1 : 0;
          const jobGone = status === 404;
          const upstreamDown = status !== undefined && status >= 500;
          const tooManyFailures = pollFailuresRef.current >= MAX_POLL_FAILURES;

          if (jobGone || upstreamDown || tooManyFailures) {
            clearTimer();
            setIsSyncing(false);
            toast({
              title: 'Sinkronisasi terputus',
              description: jobGone
                ? 'Status job tidak lagi tersedia (worker mungkin direstart).'
                : `Tidak dapat memantau status sinkronisasi: ${message}. Muat ulang halaman untuk memeriksa hasil.`,
              status: 'warning',
              duration: 8000,
              isClosable: true,
              position: 'top-right',
            });
          }
        }
      }, SAKTI_CONFIG_DEFAULTS.SYNC_POLL_INTERVAL_MS);
    },
    [api, clearTimer, notifyFinished, toast]
  );

  const startJob = useCallback(
    async (targetEntities: string[]) => {
      if (isSyncing) {
        return;
      }

      setIsSyncing(true);
      try {
        const created = await api.startSyncJob(targetEntities);
        if (!mountedRef.current) {
          return;
        }

        setJob(created);

        if (isTerminal(created.status)) {
          setIsSyncing(false);
          notifyFinished(created);
        } else {
          beginPolling(created.id);
        }
      } catch (err) {
        if (!mountedRef.current) {
          return;
        }

        setIsSyncing(false);
        const { status, message } = extractApiError(err);
        toast({
          title: status === 409 ? 'Sinkronisasi sedang berjalan' : 'Gagal memulai sinkronisasi',
          description:
            status === 409
              ? 'Sebuah sinkronisasi sedang berjalan. Tunggu hingga selesai sebelum memulai lagi.'
              : message,
          status: status === 409 ? 'warning' : 'error',
          duration: 6000,
          isClosable: true,
          position: 'top-right',
        });
      }
    },
    [api, beginPolling, isSyncing, notifyFinished, toast]
  );

  const reloadEntities = useCallback(async () => {
    setLoadingEntities(true);
    setEntitiesError(null);
    try {
      const response = await api.listSyncEntities();
      if (!mountedRef.current) {
        return;
      }
      setEntities(response.entities);
    } catch (err) {
      if (!mountedRef.current) {
        return;
      }
      setEntitiesError(extractApiError(err).message);
    } finally {
      if (mountedRef.current) {
        setLoadingEntities(false);
      }
    }
  }, [api]);

  useEffect(() => {
    reloadEntities();
  }, [reloadEntities]);

  const toggleEntity = useCallback((name: string) => {
    setSelected((prev) =>
      prev.includes(name) ? prev.filter((item) => item !== name) : [...prev, name]
    );
  }, []);

  const selectAllEntities = useCallback(() => {
    setSelected(entities.map((entity) => entity.name));
  }, [entities]);

  const clearSelection = useCallback(() => {
    setSelected([]);
  }, []);

  const syncAll = useCallback(async () => {
    await startJob([SYNC_ALL_SENTINEL]);
  }, [startJob]);

  const syncSelected = useCallback(async () => {
    if (selected.length === 0) {
      return;
    }
    await startJob(selected);
  }, [selected, startJob]);

  return {
    entities,
    loadingEntities,
    entitiesError,
    selected,
    job,
    isSyncing,
    toggleEntity,
    selectAllEntities,
    clearSelection,
    syncAll,
    syncSelected,
    reloadEntities,
  };
};

export default useSaktiSync;
